import {
    ENROLLMENT_FLOW_TYPES,
    ENROLLMENT_FLOW_STEPS,
    ENROLLMENT_STEPS
} from './constants'

/** Greenfield only — re-enrollment skips package choice (legacy form_type guard). */
const PACKAGE_CHOICE_FLOW_TYPES = new Set<ENROLLMENT_FLOW_TYPES>([
    ENROLLMENT_FLOW_TYPES.ENROLLMENT,
    ENROLLMENT_FLOW_TYPES.ENROLLMENT_WITH_VOUCHER,
    ENROLLMENT_FLOW_TYPES.ENROLLMENT_WITH_MOBILE_MONEY
])

export type ShouldShowPackageChoiceParams = {
    modification?: string
    flowType: ENROLLMENT_FLOW_TYPES
    skipForPreselectedPackets?: boolean
}

/** Visibility is driven by flow type; modification and preselected packets hide it. */
export const shouldShowPackageChoice = ({
    modification,
    flowType,
    skipForPreselectedPackets = false
}: ShouldShowPackageChoiceParams): boolean => {
    if (skipForPreselectedPackets || modification) {
        return false
    }

    return PACKAGE_CHOICE_FLOW_TYPES.has(flowType)
}

export const getEnrollmentFlowSteps = (
    flowType: ENROLLMENT_FLOW_TYPES,
    packageChoiceParams: Omit<ShouldShowPackageChoiceParams, 'flowType'>
): ENROLLMENT_STEPS[] => {
    const steps = ENROLLMENT_FLOW_STEPS[flowType]

    if (shouldShowPackageChoice({ ...packageChoiceParams, flowType })) {
        return [ENROLLMENT_STEPS.PACKAGE_CHOICE, ...steps]
    }

    return steps
}

export type StepperDisplay = {
    isPackageChoiceStep: boolean
    stepCount: number
    activeSteps: number[]
}

/**
 * The package-choice screen sits outside the visible stepper, so a flow that
 * opens with it shows one step fewer and every index shifts down by one.
 */
export const getStepperDisplay = (
    flowSteps: ENROLLMENT_STEPS[],
    activeSteps: number[],
    flowLength: number
): StepperDisplay => {
    const hasPackageChoiceStep =
        flowSteps[0] === ENROLLMENT_STEPS.PACKAGE_CHOICE

    if (!hasPackageChoiceStep) {
        return {
            isPackageChoiceStep: false,
            stepCount: flowLength,
            activeSteps
        }
    }

    return {
        isPackageChoiceStep:
            flowSteps[activeSteps.length - 1] ===
            ENROLLMENT_STEPS.PACKAGE_CHOICE,
        stepCount: flowLength - 1,
        activeSteps: activeSteps.slice(1).map((step) => step - 1)
    }
}

/** Steps that render edge to edge rather than inside the padded container. */
export const isFullBleedEnrollmentStep = (
    currentStep?: ENROLLMENT_STEPS,
    selectedProduct?: { isAddOn: boolean }
): boolean => {
    if (currentStep === ENROLLMENT_STEPS.REGISTERED_PACKAGES) {
        return true
    }

    return (
        currentStep === ENROLLMENT_STEPS.PACKET_SELECTION &&
        Boolean(selectedProduct) &&
        !selectedProduct?.isAddOn
    )
}
